import { FC } from "react";
import { Column } from "react-table";
import { Table } from "../../../../common/components/Table/Table";
import { QueryLayout } from "common/components/Layout/QueryLayout";
import { SkeletonTable } from "common/components/Table/SkeletonTable";
import { NotFoundTitle } from "common/components/Title/NotFoundTitle";
import { useGetCreditReportQuery } from "services/admin/adminReportsApi";
import { ICreditItem, IReportFounder } from "common/models/admin/IReports";
import { formatMoney } from "common/utils/helpers";

export const founderName = (founder: Nullable<IReportFounder>) =>
  founder
    ? [founder.last_name, founder.first_name, founder.middle_name]
        .filter(Boolean)
        .join(" ")
    : "—";

const sumColumn = (key: keyof ICreditItem, money = true) => (info: any) => {
  const total = info.rows.reduce(
    (sum: number, row: any) => sum + Number(row.original[key] ?? 0),
    0
  );
  return <>{money ? formatMoney(total) : total}</>;
};

// «Нам должны»: компании-заказчики. Долг — отрицательный баланс (оплата в
// пределах кредитной линии), оплачено — списания с баланса компании
export const CreditTable: FC<{ query?: string }> = ({ query }) => {
  const { data, isLoading, isFetching, error } = useGetCreditReportQuery({
    query: query || undefined,
  });

  return (
    <QueryLayout
      isLoading={isLoading}
      error={error}
      skeleton={<SkeletonTable rowCount={6} colCount={columns.length} />}
    >
      {data?.items.length ? (
        <Table data={data.items} columns={columns} isLoading={isFetching} />
      ) : (
        <NotFoundTitle />
      )}
    </QueryLayout>
  );
};

const columns: Column<ICreditItem>[] = [
  {
    Header: "#",
    accessor: "id",
    Footer: "Итого",
  },
  {
    Header: "Название организации",
    accessor: "name",
  },
  {
    Header: "ФИО руководителя",
    accessor: "founder",
    Cell: ({ row }: any) => (
      <span className="text-primary">{founderName(row.original.founder)}</span>
    ),
  },
  {
    Header: "Кредитная линия, руб.",
    accessor: "credit_line",
    Cell: ({ row }: any) => <>{formatMoney(row.original.credit_line)}</>,
  },
  {
    Header: "Завершённые заказы",
    accessor: "finished_orders_count",
    Footer: sumColumn("finished_orders_count", false),
  },
  {
    Header: "Сумма заказов, руб.",
    accessor: "orders_sum",
    Footer: sumColumn("orders_sum"),
    Cell: ({ row }: any) => <>{formatMoney(row.original.orders_sum)}</>,
  },
  {
    Header: "Оплачено с баланса, руб.",
    accessor: "paid",
    Footer: sumColumn("paid"),
    Cell: ({ row }: any) => <>{formatMoney(row.original.paid)}</>,
  },
  {
    Header: "Баланс, руб.",
    accessor: "balance",
    Footer: sumColumn("balance"),
    Cell: ({ row }: any) => (
      <span
        className={row.original.balance < 0 ? "text-red" : "text-primary"}
      >
        {formatMoney(row.original.balance)}
      </span>
    ),
  },
  {
    Header: "Долг, руб.",
    accessor: "debt",
    Footer: (info: any) => (
      <span className="text-red">{sumColumn("debt")(info)}</span>
    ),
    Cell: ({ row }: any) => (
      <span className={row.original.debt > 0 ? "text-red" : ""}>
        {formatMoney(row.original.debt)}
      </span>
    ),
  },
];
